/**
 * components/needs/FieldConditionDropdown.tsx
 *
 * Pill-style dropdown for assigning conditional display rules to a form field.
 * Supports up to 3 condition rules with configurable AND/OR logic between them.
 *
 * Each condition rule: pick a source field (single/multiple choice) + values.
 * condition_operator controls how rules are combined:
 *   'or'  → field visible if AT LEAST ONE rule is satisfied
 *   'and' → field visible only if ALL rules are satisfied simultaneously
 *
 * UI Modes:
 *   - list: stacked condition rows with operator toggle + "Ajouter" button
 *   - select-field: pick source field for a new condition
 *   - select-values: pick values (add new or edit existing condition)
 *
 * Mar 2026: Reworked from single-condition to multi-condition stacked UI.
 * Apr 2026: Added AND/OR segmented toggle between condition rows.
 * Apr 2026: Fixed ET/OU toggle - active option always uses primary-600 blue regardless of which is selected.
 */

import { useState, useRef, useEffect } from 'react';
import {
  GitBranch, ChevronDown, Check, ChevronRight,
  Circle, ListChecks, ArrowLeft, X, Plus,
} from 'lucide-react';
import type { NeedItem, FieldCondition } from '../../types/needs';

const MAX_CONDITIONS = 3;

type PanelMode = 'list' | 'select-field' | 'select-values';

function getFieldTypeIcon(type: string) {
  if (type === 'single_choice') return Circle;
  if (type === 'multiple_choice') return ListChecks;
  return null;
}

function getFieldTypeColor(type: string) {
  if (type === 'single_choice') return 'text-teal-600';
  if (type === 'multiple_choice') return 'text-emerald-600';
  return 'text-neutral-400';
}

interface FieldConditionDropdownProps {
  conditions: FieldCondition[] | null;
  conditionOperator: 'or' | 'and';
  availableFields: NeedItem[];
  onChange: (conditions: FieldCondition[] | null) => void;
  onOperatorChange: (operator: 'or' | 'and') => void;
}

export function FieldConditionDropdown({
  conditions,
  conditionOperator,
  availableFields,
  onChange,
  onOperatorChange,
}: FieldConditionDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const [mode, setMode] = useState<PanelMode>('list');
  const [pendingSourceId, setPendingSourceId] = useState<string | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setDropdownPosition(spaceBelow < 320 && rect.top > spaceBelow ? 'top' : 'bottom');
    }
  }, [isOpen]);

  const handleOpen = () => {
    setMode('list');
    setPendingSourceId(null);
    setEditingIndex(null);
    setIsOpen(!isOpen);
  };

  const handleSelectField = (fieldId: string) => {
    setPendingSourceId(fieldId);
    setMode('select-values');
  };

  const handleEditCondition = (index: number) => {
    setPendingSourceId(conditions![index].source_field_id);
    setEditingIndex(index);
    setMode('select-values');
  };

  const handleToggleValue = (value: string) => {
    if (editingIndex !== null) {
      const existing = conditions![editingIndex];
      const next = existing.values.includes(value)
        ? existing.values.filter((v) => v !== value)
        : [...existing.values, value];
      if (next.length === 0) return;
      const newConditions = conditions!.map((c, i) =>
        i === editingIndex ? { ...c, values: next } : c
      );
      onChange(newConditions);
    } else {
      const currentConditions = conditions || [];
      const existingIdx = currentConditions.findIndex(
        (c) => c.source_field_id === pendingSourceId
      );
      if (existingIdx >= 0) {
        const existing = currentConditions[existingIdx];
        const next = existing.values.includes(value)
          ? existing.values.filter((v) => v !== value)
          : [...existing.values, value];
        if (next.length === 0) return;
        const newConditions = currentConditions.map((c, i) =>
          i === existingIdx ? { ...c, values: next } : c
        );
        onChange(newConditions);
        setEditingIndex(existingIdx);
      } else {
        const newCondition: FieldCondition = {
          source_field_id: pendingSourceId!,
          values: [value],
        };
        const newConditions = [...currentConditions, newCondition];
        onChange(newConditions);
        setEditingIndex(newConditions.length - 1);
      }
    }
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = (conditions || []).filter((_, i) => i !== index);
    onChange(newConditions.length > 0 ? newConditions : null);
  };

  const handleBack = () => {
    setMode('list');
    setPendingSourceId(null);
    setEditingIndex(null);
  };

  const usedSourceIds = new Set((conditions || []).map((c) => c.source_field_id));
  const unusedAvailableFields = availableFields.filter((f) => !usedSourceIds.has(f.id));
  const canAddMore = (conditions?.length || 0) < MAX_CONDITIONS && unusedAvailableFields.length > 0;

  const pendingField = availableFields.find((f) => f.id === pendingSourceId);
  const pendingOptions = pendingField?.options || [];
  const currentValues =
    editingIndex !== null
      ? (conditions?.[editingIndex]?.values || [])
      : (conditions?.find((c) => c.source_field_id === pendingSourceId)?.values || []);

  const conditionCount = conditions?.length || 0;
  const hasConditions = conditionCount > 0;

  const operatorLabel = conditionOperator === 'and' ? 'ET' : 'OU';
  const displayLabel = hasConditions
    ? conditionCount === 1
      ? `Si ${conditions![0].values.join(' ou ')}`
      : `${conditionCount} conditions (${operatorLabel})`
    : 'Ajouter une condition';

  const pillClass = hasConditions
    ? 'bg-amber-100 text-amber-700 border-amber-200 hover:opacity-80'
    : 'bg-neutral-100 text-neutral-500 border-neutral-200 hover:opacity-80';

  return (
    <div ref={dropdownRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleOpen}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${pillClass}`}
        title="Définir les conditions d'affichage"
      >
        <GitBranch className="w-4 h-4 flex-shrink-0" />
        <span className="truncate max-w-[180px]" title={displayLabel}>{displayLabel}</span>
        <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
      </button>

      {isOpen && (
        <div
          className={`absolute left-0 w-80 bg-white rounded-xl shadow-soft-xl border border-neutral-200 py-2 z-20 ${
            dropdownPosition === 'bottom' ? 'top-full mt-2' : 'bottom-full mb-2'
          }`}
        >
          {mode === 'list' && (
            <ConditionListPanel
              conditions={conditions}
              conditionOperator={conditionOperator}
              availableFields={availableFields}
              canAddMore={canAddMore}
              onEditCondition={handleEditCondition}
              onRemoveCondition={handleRemoveCondition}
              onStartAdd={() => setMode('select-field')}
              onToggleOperator={() => onOperatorChange(conditionOperator === 'or' ? 'and' : 'or')}
            />
          )}

          {mode === 'select-field' && (
            <FieldSelectPanel
              availableFields={unusedAvailableFields}
              onBack={handleBack}
              onSelectField={handleSelectField}
            />
          )}

          {mode === 'select-values' && (
            <ValuesSelectPanel
              pendingField={pendingField}
              pendingOptions={pendingOptions}
              currentValues={currentValues}
              onBack={handleBack}
              onToggleValue={handleToggleValue}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface ConditionListPanelProps {
  conditions: FieldCondition[] | null;
  conditionOperator: 'or' | 'and';
  availableFields: NeedItem[];
  canAddMore: boolean;
  onEditCondition: (index: number) => void;
  onRemoveCondition: (index: number) => void;
  onStartAdd: () => void;
  onToggleOperator: () => void;
}

function ConditionListPanel({
  conditions,
  conditionOperator,
  availableFields,
  canAddMore,
  onEditCondition,
  onRemoveCondition,
  onStartAdd,
  onToggleOperator,
}: ConditionListPanelProps) {
  const list = conditions || [];
  const showOperatorToggle = list.length >= 2;

  return (
    <>
      <p className="px-4 py-1.5 text-xs font-semibold text-neutral-400 uppercase tracking-wide">
        Conditions d'affichage
      </p>

      {list.length === 0 && (
        <p className="px-4 py-3 text-sm text-neutral-500 italic">
          Aucune condition définie.
        </p>
      )}

      {list.map((cond, index) => {
        const sourceField = availableFields.find((f) => f.id === cond.source_field_id);
        const TypeIcon = sourceField ? getFieldTypeIcon(sourceField.type) : null;
        const typeColor = sourceField ? getFieldTypeColor(sourceField.type) : 'text-neutral-400';

        return (
          <div key={cond.source_field_id}>
            <div className="group flex items-center gap-2 px-4 py-2.5 hover:bg-neutral-50">
              {TypeIcon && (
                <TypeIcon className={`w-3.5 h-3.5 flex-shrink-0 ${typeColor}`} />
              )}
              <button
                type="button"
                onClick={() => onEditCondition(index)}
                className="flex-1 text-left text-sm min-w-0"
              >
                <span className="font-medium text-neutral-800 truncate block">
                  {sourceField?.label || 'Question supprimée'}
                </span>
                <span className="text-amber-600 text-xs">
                  {cond.values.join(', ')}
                </span>
              </button>
              <button
                type="button"
                onClick={() => onRemoveCondition(index)}
                className="opacity-0 group-hover:opacity-100 p-1 text-neutral-400 hover:text-error-500 rounded transition-all flex-shrink-0"
                title="Supprimer cette condition"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            {index < list.length - 1 && (
              <div className="flex items-center px-4 py-1">
                <div className="flex-1 h-px bg-neutral-100" />
                <div className="mx-2 flex items-center bg-neutral-100 border border-neutral-200 rounded-lg p-0.5 gap-0.5">
                  <button
                    type="button"
                    onClick={() => conditionOperator !== 'and' && onToggleOperator()}
                    className={`px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md transition-all ${
                      conditionOperator === 'and'
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'text-neutral-400 hover:text-neutral-600'
                    }`}
                  >
                    ET
                  </button>
                  <button
                    type="button"
                    onClick={() => conditionOperator !== 'or' && onToggleOperator()}
                    className={`px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md transition-all ${
                      conditionOperator === 'or'
                        ? 'bg-primary-600 text-white shadow-sm'
                        : 'text-neutral-400 hover:text-neutral-600'
                    }`}
                  >
                    OU
                  </button>
                </div>
                <div className="flex-1 h-px bg-neutral-100" />
              </div>
            )}
          </div>
        );
      })}

      {showOperatorToggle && (
        <p className="px-4 py-1 text-xs text-neutral-400">
          {conditionOperator === 'and'
            ? 'Toutes les conditions doivent être remplies simultanément.'
            : 'Au moins une condition doit être remplie.'}
        </p>
      )}

      {canAddMore && (
        <button
          type="button"
          onClick={onStartAdd}
          className={`w-full px-4 py-2.5 flex items-center gap-2 text-sm text-primary-600 hover:bg-primary-50 transition-colors ${
            list.length > 0 ? 'border-t border-neutral-100 mt-1' : ''
          }`}
        >
          <Plus className="w-4 h-4" />
          {list.length === 0 ? 'Ajouter une condition' : 'Ajouter une autre condition'}
        </button>
      )}

      {!canAddMore && list.length === 0 && (
        <p className="px-4 py-3 text-sm text-neutral-500 italic">
          Ajoutez d'abord une question à choix unique ou multiple dans ce formulaire.
        </p>
      )}
    </>
  );
}

interface FieldSelectPanelProps {
  availableFields: NeedItem[];
  onBack: () => void;
  onSelectField: (fieldId: string) => void;
}

function FieldSelectPanel({ availableFields, onBack, onSelectField }: FieldSelectPanelProps) {
  return (
    <>
      <div className="px-4 py-2 border-b border-neutral-100">
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-neutral-500 hover:text-neutral-700 flex items-center gap-1 mb-1"
        >
          <ArrowLeft className="w-3 h-3" /> Retour
        </button>
        <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">
          Conditionner selon la réponse à...
        </p>
      </div>

      {availableFields.length === 0 ? (
        <p className="px-4 py-3 text-sm text-neutral-500 italic">
          Toutes les questions disponibles sont déjà utilisées.
        </p>
      ) : (
        <div className="max-h-60 overflow-y-auto">
          {availableFields.map((field) => {
            const TypeIcon = getFieldTypeIcon(field.type);
            const typeColor = getFieldTypeColor(field.type);
            return (
              <button
                key={field.id}
                type="button"
                onClick={() => onSelectField(field.id)}
                className="w-full flex items-start justify-between gap-2 px-4 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                <span className="flex items-start gap-2 flex-1 min-w-0">
                  {TypeIcon && (
                    <TypeIcon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${typeColor}`} />
                  )}
                  <span className="text-left line-clamp-2">{field.label}</span>
                </span>
                <ChevronRight className="w-4 h-4 flex-shrink-0 text-neutral-400 mt-0.5" />
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

interface ValuesSelectPanelProps {
  pendingField: NeedItem | undefined;
  pendingOptions: string[];
  currentValues: string[];
  onBack: () => void;
  onToggleValue: (value: string) => void;
}

function ValuesSelectPanel({
  pendingField,
  pendingOptions,
  currentValues,
  onBack,
  onToggleValue,
}: ValuesSelectPanelProps) {
  return (
    <>
      <div className="px-4 py-2 border-b border-neutral-100">
        <button
          type="button"
          onClick={onBack}
          className="text-xs text-neutral-500 hover:text-neutral-700 flex items-center gap-1 mb-1.5"
        >
          <ArrowLeft className="w-3 h-3" aria-hidden="true" /> Retour
        </button>
        <p className="text-sm font-medium text-neutral-700 line-clamp-2">
          {pendingField?.label}
        </p>
      </div>
      <p className="px-4 py-1.5 text-xs font-semibold text-neutral-400 uppercase tracking-wide">
        Afficher si la réponse est...
      </p>
      {pendingOptions.length === 0 ? (
        <p className="px-4 py-3 text-sm text-neutral-500 italic">
          Cette question n'a pas d'options définies.
        </p>
      ) : (
        <div className="max-h-52 overflow-y-auto">
          {pendingOptions.map((option) => {
            const isSelected = currentValues.includes(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => onToggleValue(option)}
                className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-neutral-50 transition-colors ${
                  isSelected ? 'text-amber-700 font-medium bg-amber-50' : 'text-neutral-700'
                }`}
              >
                <div
                  className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${
                    isSelected ? 'bg-amber-500 border-amber-500' : 'border-neutral-300'
                  }`}
                >
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className="flex-1 text-left">{option}</span>
              </button>
            );
          })}
        </div>
      )}
      {currentValues.length > 0 && (
        <div className="px-4 py-2 border-t border-neutral-100">
          <p className="text-xs text-neutral-500">
            Afficher si :{' '}
            <span className="font-medium text-amber-700">{currentValues.join(', ')}</span>
          </p>
        </div>
      )}
    </>
  );
}
